<script setup lang="ts">
import { computed, onMounted, provide, ref, watch } from 'vue';
import { useRoute, useRouter, type RouteParamsRaw } from 'vue-router';
import { useElementSize, useScroll } from '@vueuse/core';
import { useErhlLoanAppProcess } from '@/helpers/useErhlLoanAppProcess';
import AppHeader from '@/components/layout/AppHeader.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import ErhlProgressNav from '@/views/erhl-application/modules/ErhlProgressNav.vue';
import Login from '@/views/erhl-application/modules/LoginModal/Login.vue';
import { CurrentKey } from '@/helpers/const';
import api from '@/api';
import { useCookies } from '@vueuse/integrations/useCookies';
import { LoanType } from '@/helpers/const';
import { ErhlApplicationProcessSteps, ErhlProcessStep, type ErhlProcessStepEnum, type LoanApplication } from '@/types';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';

const { send, service, state, stepper, currentStep } = useErhlLoanAppProcess();
const { identify, setErhlAccess } = useFeatureFlags();
const { y, directions } = useScroll(window);
const cookies = useCookies();
const { showSessionExpiredModal, flagSessionExpired, clearSessionExpired } = useSessionExpiry();
const bootstrapFailed = ref(false);
const loginEmail = computed(() => state.value.context.loanApp.brokerEmail ?? '');

const hideProgressNav = ref(false);
watch(y, (currY, oldY) => {
  const delta = Math.abs(currY - oldY);
  if (directions.bottom && currY > 100) {
    hideProgressNav.value = true;
  }
  if (directions.top && (currY <= 100 || delta > 10)) {
    hideProgressNav.value = false;
  }
});

provide('navInfo', { mainScrollOffset: y });

const navRef = ref(null);
const { height } = useElementSize(navRef);
const navheight = computed(() => `${height.value - y.value}px`);

const appVersion = computed(() => import.meta.env.VITE_APP_VERSION);

const route = useRoute();
const router = useRouter();

const showNav = computed(
  () => route.path !== '/' && !route.path.startsWith('/confirmation') && route.name !== 'erhlCompleted',
);

const gotoStep = (step: ErhlProcessStepEnum) => {
  const stepRoute = ErhlApplicationProcessSteps[step]?.route;
  if (stepRoute && stepRoute !== route.name) {
    router.push({ name: stepRoute });
  }
};

/**
 * Resolves the `finishType` route param for the ERHL completed page based on
 * the loan application's user type, action type, and loan type, plus the
 * source context (e.g. CRM vs. broker portal).
 *
 * @param loanApp - Current loan application snapshot.
 * @param fromContext - The `state.context.from` value (e.g. `'crm'`).
 * @returns The string used as `:finishType` route param.
 */
const resolveFinishType = (loanApp: LoanApplication, fromContext: string | undefined): string => {
  if (loanApp.userType === 'Broker') return 'broker';

  if (loanApp.actionType === 'updateExisting') {
    return fromContext === 'crm' ? 'income_expense' : 'user_declaration';
  }

  switch (loanApp.loanType) {
    case LoanType.BRIDGING:
      return 'bridge_loan';
    default:
      return 'equity_release';
  }
};

watch(
  () => route.name,
  (newRoute, oldRoute) => {
    if (newRoute === oldRoute) return;

    // Once the application is submitted, prevent browser back navigation from
    // re-entering any step. Mirrors broker layout behavior where the user is
    // always redirected forward to the confirmation page after submission.
    if (state.value.context.current === ErhlProcessStep.Finished && newRoute !== ErhlProcessStep.Finished) {
      const finishType = resolveFinishType(state.value.context.loanApp, state.value.context.from);
      router.replace({ name: ErhlProcessStep.Finished, params: { finishType } });
      return;
    }

    const step = stepper.value.find((s) => s.route === newRoute)?.id;
    if (step) {
      send({ type: 'GOTO', step });
    }
  },
  { immediate: true },
);

const bootstrapErhlBroker = async () => {
  const appKey = localStorage.getItem(CurrentKey) ?? '';
  const savedStateRaw = localStorage.getItem(appKey);
  const newState = savedStateRaw ? JSON.parse(savedStateRaw) : { ...state.value.context };

  let brokerId = '';
  let brokerFullName = '';
  let brokerReferenceNumber = '';
  let brokerEmail = '';
  let erhlAccess: boolean | undefined;

  try {
    const { data } = await api.backend.brokers.user.currentUser();
    const brokerUser = data.broker;

    brokerId = brokerUser.id ?? '';
    brokerFullName = [brokerUser.firstName, brokerUser.lastName].filter(Boolean).join(' ');
    brokerReferenceNumber = 'brokerReferenceNumber' in brokerUser ? brokerUser.brokerReferenceNumber ?? '' : '';
    brokerEmail = brokerUser.email ?? '';
    erhlAccess = 'hasErhlAccess' in brokerUser ? brokerUser.hasErhlAccess : undefined;
    const selectedBrokerId = cookies.get('broker-id');

    if (
      'brokerAssistantReferenceNumber' in brokerUser &&
      brokerUser.brokerAssistantReferenceNumber &&
      selectedBrokerId
    ) {
      const { data: brokerData } = await api.backend.brokerAssistants.broker.retrieveBroker({ id: selectedBrokerId });
      brokerId = selectedBrokerId;
      brokerFullName = [brokerData.broker.firstName, brokerData.broker.lastName].filter(Boolean).join(' ');
      brokerEmail = brokerData.broker.email ?? '';
      brokerReferenceNumber = brokerData.broker.brokerReferenceNumber ?? '';
      // Access belongs to the broker being acted for, never the assistant.
      erhlAccess = brokerData.broker.hasErhlAccess;
    }
  } catch (error) {
    if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
      bootstrapFailed.value = true;
      flagSessionExpired();
      return;
    }
    throw error;
  }

  // Identify the resolved broker to Flagsmith so identity-based segments are
  // evaluated for the duration of the ERHL flow. No traits are sent from the
  // frontend — the BE owns trait state.
  await identify(brokerId);

  // Record the resolved ERHL access from the responses already fetched above, so this
  // layout answers the gate for a broker who reached it without going through
  // resolveBrokerIdentity. Set after identify(), which clears it on an identity change.
  setErhlAccess(erhlAccess);

  const currentLoanApp = state.value.context.loanApp;
  const newLoanApp = {
    ...currentLoanApp,
    brokerId,
    brokerReferenceNumber,
    brokerFullName,
    brokerEmail,
  };
  newState.loanApp = newLoanApp;

  const savedStep = newState?.current as ErhlProcessStepEnum | undefined;
  const savedRoute = savedStep ? ErhlApplicationProcessSteps[savedStep]?.route : null;
  const targetRoute = savedRoute ?? 'erhlReady';

  let targetParams: RouteParamsRaw | undefined;
  const fromContext = newState?.from ?? state.value.context.from;
  if (targetRoute === ErhlProcessStep.Finished) {
    targetParams = { finishType: resolveFinishType(newLoanApp, fromContext) };
  }

  if (route.name !== targetRoute) {
    router.push({ name: targetRoute, params: targetParams });
  }

  send({ type: 'RELOAD', state: newState });
};

onMounted(bootstrapErhlBroker);

const onLoginModalClose = () => {
  clearSessionExpired();
  if (bootstrapFailed.value) {
    bootstrapFailed.value = false;
    bootstrapErhlBroker();
  }
};

service.onTransition((transition) => {
  if (transition.value !== 'gotoReady' && transition.value !== 'start') {
    const loanApp = transition.context.loanApp;
    const key = transition.value as ErhlProcessStepEnum;
    const name = ErhlApplicationProcessSteps[key]?.route;
    if (!name) return;

    let params: RouteParamsRaw | undefined;
    if (name === ErhlProcessStep.Finished) {
      params = { finishType: resolveFinishType(loanApp, transition.context.from) };
    }

    if (route.name !== name) {
      if (name === 'erhlCompleted') {
        router.replace({ name, params });
      } else {
        router.push({ name, params });
      }
    }
  }
});
</script>

<template>
  <div class="app-layout" :class="{ 'app-layout--hide-nav': !showNav }">
    <Login v-model:modelValue="showSessionExpiredModal" :broker-email="loginEmail" @close="onLoginModalClose" />
    <header class="app-layout__header">
      <AppHeader />
    </header>
    <nav ref="navRef" class="app-layout__nav" :class="{ hide: hideProgressNav }">
      <div class="nav-container">
        <ErhlProgressNav
          v-if="showNav"
          :model-value="currentStep"
          :end-debt="state.context.loanApp.endDebt"
          :steps="stepper"
          class="my-6"
          @update:model-value="gotoStep"
        />
      </div>
    </nav>
    <main class="app-layout__main">
      <RouterView v-slot="{ Component }">
        <Transition name="fade">
          <div v-if="Component" :key="route.fullPath" style="width: 100%">
            <component :is="Component" />
          </div>
        </Transition>
      </RouterView>
    </main>
    <footer class="app-layout__footer">
      <AppFooter :is-broker="state.context.loanApp.userType === 'Broker'" />
      <div class="app-version">v{{ appVersion }}</div>
    </footer>
  </div>
</template>

<style lang="scss" scoped>
.app-layout {
  display: grid;
  grid-template:
    'header' minmax(64px, auto) 'nav   ' 100px
    'main  ' 1fr
    'footer' auto / 1fr;

  width: 100vw;
  min-height: 100vh;

  // `clip` (unlike `hidden`) doesn't trigger the CSS overflow-x/overflow-y interop rule that
  // silently computes the other axis to `auto` — that rule was turning this element into its
  // own scroll container, which stole the sticky EquitySnapshot's "nearest scrolling ancestor"
  // away from the real window scroll, breaking its pinned-to-top behaviour.
  overflow-x: clip;

  &__header {
    @apply z-30 border-b border-gray-100 bg-white;
    grid-area: header;

    position: fixed;
    top: 0;
    left: 0;
    right: 0;

    height: 68px;
  }

  &__nav {
    @apply bg-bridgit-navy;
    @apply pl-0 md:pl-4 lg:pl-8;
    @apply z-20 flex justify-end;
    grid-area: nav;

    transition: top 0.3s ease-in-out;
  }

  &__main {
    @apply bg-bridgit-paleBlue;
    @apply relative flex px-4 py-6 md:px-6 lg:px-8;
    grid-area: main;
  }

  &__footer {
    @apply relative z-30 bg-white;
    grid-area: footer;
  }

  &.app-layout--hide-nav {
    grid-template:
      'header' minmax(64px, auto) 'main  ' 1fr
      'footer' auto / 1fr;

    .app-layout__nav {
      display: none;
    }

    .app-layout__main {
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    &__nav {
      position: fixed;
      top: 68px;
      left: 0;
      right: 0;
      z-index: 100;

      height: 100px;

      &.hide {
        top: -4rem;

        transition: top 0.3s ease-in-out;
      }
    }
  }

  @screen md {
    grid-template:
      'header header' minmax(64px, auto) 'nav main     ' 1fr
      'footer footer' minmax(140px, auto) / 2fr 5fr;
  }
}

:deep(.section-panel) {
  background-color: #fff;

  @media (max-width: 767px) {
    padding: 1rem;
  }

  @media (min-width: 768px) {
    --border-radius: 0.5rem;
    --border-color: #ccc;

    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
  }
}

.nav-container {
  @apply w-full overflow-auto md:fixed md:top-16 md:w-auto;

  @screen md {
    height: v-bind(navheight);
  }
}

.app-version {
  @apply text-gray-200;
  position: absolute;
  bottom: 0;
  right: 0;

  font-size: 8px;
}
</style>
